import { Field, FieldProps } from 'formik';
import { InputText } from '../InputText';
import { useEffect, useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { SearchFilterEnum } from 'api/actor';
import { actorsAPI } from 'services/swagger-api';
import { ExistenceCheckConfig } from 'types/form/componentConfig';

interface FormikInputTextProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'disabled'> {
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean | ((values: any) => boolean);
  type?: string;
  existenceCheck?: ExistenceCheckConfig
  pattern?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

// Service pour vérifier l'existence avec votre API
const checkExistence = async (value: string, searchFilter: SearchFilterEnum): Promise<boolean> => {
  try {
    const isDataExistingCommandDto = {
      searchString: value,
      searchFilter: searchFilter
    }
    const exists = await actorsAPI.isDataExisting({
      isDataExistingCommandDto
    });
    return exists;
  } catch (error) {
    console.error('Erreur vérification existence:', error);
    return false;
  }
};

// Fonction utilitaire pour valider de manière synchrone
const validateFieldSync = (value: string, name: string, validationSchema: any, formValues: any): boolean => {
  try {
    const tempValues = { ...formValues, [name]: value };
    validationSchema.validateSyncAt(name, tempValues);
    return true;
  } catch (error) {
    return false;
  }
};

export const FormikInputText: React.FC<FormikInputTextProps> = ({
  name,
  label,
  required,
  placeholder,
  disabled,
  type = 'text',
  pattern,
  onChange,
  existenceCheck,
  min,
  max,
  onBlur
}) => {
  const { t } = useTranslation([NsKey.Borrower, NsKey.Common, NsKey.ActorForm]);

  // Access the validation schema
  const [isChecking, setIsChecking] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const initialValueRef = useRef<string>('');
  const isInitializedRef = useRef<boolean>(false);

  // Cleanup au démontage
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Fonction utilitaire pour identifier les erreurs d'existence
  const isExistenceError = useCallback((error: string): boolean => {
    if (!existenceCheck) return false;
    return error.includes('existe déjà') ||
      error === existenceCheck.errorMessage ||
      error.includes('adresse email existe déjà') ||
      error.includes('numéro de téléphone existe déjà');
  }, [existenceCheck]);

  // Fonction debounce améliorée
  const debouncedExistenceCheck = useCallback((
    value: string,
    setFieldError: (field: string, message: string | undefined) => void,
    form: any
  ) => {
    if (!existenceCheck?.enabled) return;
    // Ne pas déclencher l'existence check si c'est la valeur initiale
    if (value === initialValueRef.current) {
      // Ne pas effacer l'erreur ici, car il pourrait y avoir d'autres erreurs de validation
      setIsChecking(false);
      return;
    }

    // Annuler le timeout précédent
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Validation immédiate et synchrone avant le debounce
    if (value && value.length >= 3 && form.validationSchema) {
      const isValidFormat = validateFieldSync(value, name, form.validationSchema, form.values);
      if (!isValidFormat) {
        setIsChecking(false);
        return; // Ne pas faire l'appel API si le format est invalide
      }
    }

    // Créer un nouveau timeout seulement si la validation de format passe
    timeoutRef.current = setTimeout(async () => {
      if (!value || value.length < 3) {
        // Effacer seulement les erreurs d'existence
        const currentError = form.errors[name] as string;
        if (currentError && isExistenceError(currentError)) {
          setFieldError(name, undefined);
        }
        setIsChecking(false);
        return;
      }

      try {
        form.setFieldError('isChecking', 'Checking field');
        setIsChecking(true);

        // Double vérification avec validation async (au cas où)
        if (form.validationSchema) {
          const tempValues = { ...form.values, [name]: value };
          await form.validationSchema.validateAt(name, tempValues);
        }

        // Faire l'appel API
        const exists = await checkExistence(value, existenceCheck.searchFilter);

        if (exists) {
          const errorMessage = existenceCheck.errorMessage ||
            `Cette ${existenceCheck.type === 'email' ? 'adresse email' :
              existenceCheck.type === 'phone' ? 'numéro de téléphone' :
                'valeur'} existe déjà`;
          setFieldError(name, errorMessage);
        } else {
          // Effacer seulement les erreurs d'existence
          const currentError = form.errors[name] as string;
          if (currentError && isExistenceError(currentError)) {
            setFieldError(name, undefined);
          }
        }
      } catch (validationError) {
        console.error(validationError)
      } finally {
        setIsChecking(false);
        setFieldError('isChecking', undefined);
      }
    }, existenceCheck.debounceMs || 500);
  }, [existenceCheck, name, isExistenceError]);

  return (
    <Field name={name}>
      {({ field, meta, form }: FieldProps) => {
        // Mémoriser la valeur initiale au premier rendu
        if (!isInitializedRef.current) {
          initialValueRef.current = field.value || '';
          isInitializedRef.current = true;
        }

        // Résolution de la condition disabled dynamique
        const isDisabled = typeof disabled === 'function'
          ? disabled(form.values)
          : disabled;

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          field.onChange(e);

          if (onChange) {
            onChange(e);
          }

          if (existenceCheck?.enabled) {
            // Effacer seulement les erreurs d'existence lors de la frappe
            if (form.errors[name] && typeof form.errors[name] === 'string') {
              const currentError = form.errors[name] as string;
              if (isExistenceError(currentError)) {
                form.setFieldError(name, undefined);
              }
            }

            // Déclencher le debounce avec validation préalable
            debouncedExistenceCheck(e.target.value, form.setFieldError, form);
          }
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
          field.onBlur(e);

          if (onBlur) {
            onBlur(e);
          }
        };

        // Afficher l'erreur
        const finalError = (() => {
          if (!meta.error) return '';

          // Toujours afficher les erreurs d'existence (même si pas touched)
          if (typeof meta.error === 'string' && isExistenceError(meta.error)) {
            return meta.error;
          }

          // Pour les autres erreurs, attendre que le champ soit touché
          return meta.touched ? meta.error : '';
        })();

        // Traduire l'erreur si c'est une clé de traduction
        const translatedError = finalError ? t(finalError, { defaultValue: finalError }) : '';

        return (
          <div className="relative">
            <InputText
              {...field}
              type={type}
              label={label}
              placeholder={placeholder}
              disabled={isDisabled}
              required={required}
              min={min}
              max={max}
              onChange={handleChange}
              onBlur={handleBlur}
              error={translatedError}
              isLoading={isChecking}
            />
          </div>
        );
      }}
    </Field>
  )
};
